import {
    StorageSerializers,
    until,
    useLocalStorage,
    watchDebounced,
    type Serializer,
} from '@vueuse/core';
import {defineStore} from 'pinia';
import {computed, ref, watch, type Ref} from 'vue';
import {useUserStore} from './userStore';
import UserRepository from '../api/repositories/UserRepository';
import type {UserPreferences} from '../api/models/user/userPreferences';
import {computedAsyncWithReload} from '../shared/composables/computedAsyncWithReload';
import {useBookingSunSetting} from '@/shared/composables/useBookingSunsetting';

export const useUserPreferenceStore = defineStore('userPreferences', () => {
    const userStore = useUserStore();

    const {isOldBookingDeactivated} = useBookingSunSetting();

    const loading = ref(true);

    const defaultValue: UserPreferences = {
        adminNavigationCollapsed: false,
        adminDesklyConnectPreview: false,
        frontstageNavigationCollapsed: false,
        newBooking: isOldBookingDeactivated.value,
        showNewBookingHint: false,
        showNewBookingTutorialHint: false,
        showNewBookingTutorialVideo: false,
    };

    const {state: userPreferences, reload: reloadPreferences} = computedAsyncWithReload(
        async () => {
            if (!userStore.isAuthenticated || !userStore.user?.id) {
                return defaultValue;
            }

            // Awaiting something will prevent the request below to be fired on locale change
            await Promise.resolve();

            return await UserRepository().getPreferences();
        },
        defaultValue,
        {
            evaluating: loading,
        }
    );

    const newBookingPreference = computed(
        () => isOldBookingDeactivated.value || userPreferences.value.newBooking
    );

    async function newBookingPreferenceReady() {
        if (newBookingPreference.value) {
            return true;
        }
        await until(loading).not.toBeTruthy();
        return newBookingPreference.value;
    }

    const showNewBookingHintPreference = useUserPreference(userPreferences, 'showNewBookingHint', {
        storage: {
            key: 'showNewBookingHint',
            serializer: StorageSerializers.boolean,
        },
        debounceTime: 500,
    });

    const frontstageNavigationCollapsed = useUserPreference(
        userPreferences,
        'frontstageNavigationCollapsed',
        {
            storage: {
                key: 'frontstageNavigation',
                serializer: StorageSerializers.boolean,
            },
        }
    );

    const adminNavigationCollapsed = useUserPreference(
        userPreferences,
        'adminNavigationCollapsed',
        {
            storage: {
                key: 'adminNavigation',
                serializer: StorageSerializers.boolean,
            },
        }
    );

    const showNewBookingTutorialVideo = useUserPreference(
        userPreferences,
        'showNewBookingTutorialVideo',
        {
            storage: {
                key: 'showNewBookingTutorialVideo',
                serializer: StorageSerializers.boolean,
            },
            debounceTime: 0,
        }
    );

    const adminDesklyConnectPreview = useUserPreference(
        userPreferences,
        'adminDesklyConnectPreview',
        {
            storage: {
                key: 'adminDesklyConnectPreview',
                serializer: StorageSerializers.boolean,
            },
        }
    );

    return {
        userPreferences,
        loading,
        reloadPreferences,
        frontstageNavigationCollapsed,
        adminNavigationCollapsed,
        adminDesklyConnectPreview,
        newBookingPreference,
        newBookingPreferenceReady,
        showNewBookingHintPreference,
        showNewBookingTutorialVideo,
    };
});

type UseUserPreferenceOptions<T> = {
    /**
     * The debounce time (in ms) before saving the preference
     */
    debounceTime?: number;
    /**
     * Storage configuration for persisting the preference in local storage.
     */
    storage?: {
        key: string;
        serializer?: Serializer<T>;
    };
};

/**
 * Manages user preferences with debounced updates and optional local storage.
 * Writing to the returned ref will update the corresponding user preference on the server
 * after the passed debounce time.
 * If storage options are passed, these will be used as value before the preferences are loaded.
 *
 * @template TKey - The key of the user preference.
 * @param {TKey} key - The user preference key to manage.
 * @returns A reactive reference to the user preference value.
 */
export function useUserPreference<TKey extends keyof UserPreferences>(
    userPreferences: Ref<UserPreferences>,
    key: TKey,
    options?: UseUserPreferenceOptions<UserPreferences[TKey]>
) {
    const {debounceTime = 5000, storage} = options ?? {};

    const defaultValue = userPreferences.value[key];

    const preference = storage
        ? useLocalStorage<UserPreferences[TKey]>('userPreference:' + storage.key, defaultValue, {
              serializer: storage.serializer,
              listenToStorageChanges: false,
          })
        : ref<UserPreferences[TKey]>(defaultValue);

    watch(userPreferences, value => {
        preference.value = value[key];
    });

    watchDebounced(
        preference,
        async value => {
            if (value === userPreferences.value[key]) {
                return;
            }

            const requestBody: Partial<UserPreferences> = {[key]: value};
            await UserRepository().updatePreferences(requestBody);
            userPreferences.value = {...userPreferences.value, ...requestBody};
        },
        {debounce: debounceTime}
    );

    return preference;
}
